import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  baseReducers,
  getIdOrModelId,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'
import { FormQuestionOption } from '../../models/membership/Form'
import { formQuestionOptionSchema } from '../../models/schema'
import {
  createFormQuestionOption as createFormQuestionOptionAPI,
  deleteFormQuestionOption as deleteFormQuestionOptionAPI,
  fetchFormQuestionOptions,
  updateFormQuestionOption as updateFormQuestionOptionAPI,
} from '../../services/membership/FormQuestionOptionService'
import { AppDispatch, RootState } from '../store'

const initialState: SBState<FormQuestionOption> = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'formQuestionOption',
  initialState,
  reducers: baseReducers,
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getFormQuestionOptionsSuccess,
  setQuery: setFormQuestionOptionQuery,
  reset: resetFormQuestionOptionState,
  resetQueryAndIds: resetFormQuestionOptionQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: FormQuestionOption }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectFormQuestionOptions = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedFormQuestionOption = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectFormQuestionOptionById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectFormQuestionOptionsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )

/**
 * Actions
 */

export const setSelectedFormQuestionOption =
  (formQuestionOption: FormQuestionOption | number) =>
  async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(
        getIdOrModelId<FormQuestionOption>(formQuestionOption)
      )
    )

export const getFormQuestionOptions = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchFormQuestionOptions(params),
    [formQuestionOptionSchema],
    slice.actions
  )

export const createFormQuestionOption = (formQuestionOption: FormData) =>
  SBAPI.CreateDispatched(
    createFormQuestionOptionAPI(formQuestionOption),
    formQuestionOptionSchema,
    slice.actions
  )

export const updateFormQuestionOption = (formQuestionOption: FormData) =>
  SBAPI.UpdateDispatched<FormQuestionOption>(
    updateFormQuestionOptionAPI(formQuestionOption),
    formQuestionOption,
    slice.actions
  )

export const deleteFormQuestionOption = (
  formQuestionOption: FormQuestionOption
) =>
  SBAPI.DestroyDispatched<FormQuestionOption>(
    deleteFormQuestionOptionAPI(formQuestionOption.id),
    formQuestionOption,
    slice.actions
  )
